import { Component, Input } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

type ResourceType = 'material' | 'dailyTask' | 'quotient' | 'webinar';

interface WebinarLanguageEntry {
  label: string;
  title: string;
  expertName: string;
  description: string;
  expertBio: string;
  hasContent: boolean;
}

interface TaskLanguageEntry {
  label: string;
  title: string;
  description: string;
  hasContent: boolean;
}

@Component({
  selector: 'resource-details-modal',
  templateUrl: './resource-details-modal.component.html',
  styleUrls: ['./resource-details-modal.component.scss'],
})
export class ResourceDetailsModalComponent {
  @Input() item: any = {};
  @Input() resourceType: ResourceType = 'material';
  @Input() defaultLanguage: any;
  @Input() languages: any[] = [];

  constructor(private modalService: NgbModal) {}

  cancelModal() {
    this.modalService.dismissAll();
  }

  get eyebrow(): string {
    const labels: Record<ResourceType, string> = {
      material: 'Material Details',
      dailyTask: 'Daily Task Details',
      quotient: 'Quotient Details',
      webinar: 'Webinar Details',
    };
    return labels[this.resourceType];
  }

  get title(): string {
    if (this.resourceType === 'dailyTask') return this.getLocalizedValue(this.item?.title, 'Untitled task');
    if (this.resourceType === 'webinar') return this.getLocalizedValue(this.item?.title, 'Untitled webinar');
    return this.getLocalizedValue(this.item?.name, `Untitled ${this.resourceType}`);
  }

  get subtitle(): string {
    if (this.resourceType === 'webinar') return this.getLocalizedValue(this.item?.expertName, 'No expert added');
    if (this.resourceType === 'material') return this.getMaterialType();
    if (this.resourceType === 'dailyTask') return this.formatLabel(this.item?.taskType || 'Daily task');
    return '';
  }

  get idLabel(): string {
    const idMap: Record<ResourceType, string> = {
      material: 'materialID',
      dailyTask: 'taskID',
      quotient: 'quotientID',
      webinar: 'webinarID',
    };
    return this.item?.[idMap[this.resourceType]] || '-';
  }

  get description(): string {
    return this.getLocalizedValue(this.item?.description, 'No description provided.');
  }

  get image(): string {
    return this.item?.image || '';
  }

  get showMediaPanel(): boolean {
    return this.resourceType !== 'dailyTask';
  }

  get status(): string {
    return this.item?.status || 'ACTIVE';
  }

  get isActive(): boolean {
    return this.status === 'ACTIVE';
  }

  get stats(): Array<{ label: string; value: string }> {
    if (this.resourceType === 'material') {
      return [
        { label: 'Type', value: this.getMaterialType() },
      ];
    }

    if (this.resourceType === 'dailyTask') {
      return [
        { label: 'Time', value: this.formatLabel(this.item?.timeOfDay) },
        { label: 'Duration', value: `${this.item?.estimatedDurationMinutes || 5} min` },
      ];
    }

    if (this.resourceType === 'webinar') {
      return [
        { label: 'Schedule', value: this.getScheduleLabel() },
        { label: 'Duration', value: this.item?.durationMinutes ? `${this.item.durationMinutes} min` : '-' },
        { label: 'Access', value: this.item?.accessLevel || 'FREE' },
      ];
    }

    return [
      { label: 'Created', value: this.getDateLabel(this.item?.createdAt) },
      { label: 'Updated', value: this.getDateLabel(this.item?.updatedAt) },
      { label: 'Status', value: this.status },
    ];
  }

  get detailRows(): Array<{ label: string; value: string; isLink?: boolean }> {
    if (this.resourceType === 'material') {
      return [
        { label: 'How to Use', value: this.getLocalizedValue(this.item?.howToUse, 'No usage instructions provided.') },
      ];
    }

    if (this.resourceType === 'dailyTask') {
      return [
        { label: 'Importance', value: this.formatLabel(this.item?.importanceLevel) },
        { label: 'Access', value: this.item?.accessLevel || '-' },
        { label: 'Sort Order', value: this.item?.sortOrder || '-' },
      ];
    }

    if (this.resourceType === 'webinar') {
      return [
        { label: 'Webinar ID', value: this.idLabel },
        { label: 'Max Participants', value: this.item?.maxParticipants || '-' },
        { label: 'Live Now', value: this.item?.isLive ? 'Yes' : 'No' },
        { label: 'Webinar URL', value: this.item?.webinarUrl || '-', isLink: !!this.item?.webinarUrl },
        { label: 'Recording URL', value: this.item?.recordingUrl || '-', isLink: !!this.item?.recordingUrl },
      ];
    }

    return [];
  }

  get webinarLanguageEntries(): WebinarLanguageEntry[] {
    const languages = this.getDisplayLanguages(['title', 'expertName', 'description', 'expertBio']);
    return languages.map((language: any) => {
      const languageId = language.id?.toString();
      return {
        label: this.getNestedLabel(language) || `Language ${languageId}`,
        title: this.getLocalizedValueForLanguage(this.item?.title, languageId, 'No title added'),
        expertName: this.getLocalizedValueForLanguage(this.item?.expertName, languageId, 'No expert added'),
        description: this.getLocalizedValueForLanguage(this.item?.description, languageId, 'No description provided.'),
        expertBio: this.getLocalizedValueForLanguage(this.item?.expertBio, languageId, 'No expert bio provided.'),
        hasContent: this.hasLanguageContent(languageId, ['title', 'expertName', 'description', 'expertBio']),
      };
    });
  }

  get taskLanguageEntries(): TaskLanguageEntry[] {
    const languages = this.getDisplayLanguages(['title', 'description']);
    return languages.map((language: any) => {
      const languageId = language.id?.toString();
      return {
        label: this.getNestedLabel(language) || `Language ${languageId}`,
        title: this.getLocalizedValueForLanguage(this.item?.title, languageId, 'No title added'),
        description: this.getLocalizedValueForLanguage(this.item?.description, languageId, 'No description provided.'),
        hasContent: this.hasLanguageContent(languageId, ['title', 'description']),
      };
    });
  }

  getLocalizedValue(value: any, fallback: string = '-'): string {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    return value[this.defaultLanguage] || (Object.values(value).find((item: any) => !!item) as string) || fallback;
  }

  getInitials(value: string = ''): string {
    const words = value.trim().split(/\s+/).filter(Boolean);
    if (!words.length) return this.resourceType.slice(0, 2).toUpperCase();
    return words.slice(0, 2).map((word) => word[0]?.toUpperCase()).join('');
  }

  getMaterialType(): string {
    return this.item?.materialType?.value || this.item?.materialType?.code || this.item?.type || '-';
  }

  getNestedLabel(value: any): string {
    return value?.label || value?.value || value?.code || value?.name || '';
  }

  formatLabel(value: any): string {
    if (!value) return '-';
    return value.toString().replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (char: string) => char.toUpperCase());
  }

  getScheduleLabel(): string {
    if (!this.item?.scheduledDatetime) return '-';
    return new Date(this.item.scheduledDatetime).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  getDateLabel(value: any): string {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  onImageError(event: any) {
    event.target.style.display = 'none';
  }

  private getDisplayLanguages(fields: string[]): any[] {
    if (this.languages?.length) return this.languages;
    return this.getLanguageIdsFromFields(fields).map((id) => ({ id, label: `Language ${id}` }));
  }

  private getLanguageIdsFromFields(fields: string[]): string[] {
    const ids = new Set<string>();
    fields.forEach((field) => {
      const value = this.item?.[field];
      if (value && typeof value === 'object') {
        Object.keys(value).forEach((key) => ids.add(key));
      }
    });
    if (!ids.size && this.defaultLanguage) ids.add(this.defaultLanguage.toString());
    return Array.from(ids);
  }

  private getLocalizedValueForLanguage(value: any, languageId: string, fallback: string): string {
    if (!value) return fallback;
    if (typeof value === 'string') return value || fallback;
    return value[languageId] || fallback;
  }

  private hasLanguageContent(languageId: string, fields: string[]): boolean {
    return fields.some((field) => {
      const value = this.item?.[field];
      if (!value) return false;
      if (typeof value === 'string') return !!value.trim();
      return !!value[languageId];
    });
  }
}
